import type { Emitter } from "@core/events/emitter";
import type {
  AuthTokens,
  ConfirmResetPasswordRequest,
  ConfirmSignUpRequest,
  IdentityProvider,
  ResetPasswordRequest,
  SignInRequest,
  SignInResult,
  SignUpRequest,
  UserInfo,
} from "./types";

/**
 * `IAuthModel` — web port of Unity's `Models/AuthModel/IAuthModel.cs`,
 * trimmed to the surface actually consumed by the editor + login UI.
 *
 * Intentional differences from Unity:
 *   - Public surface returns `Promise<T>` instead of C# `Task<T>` and emits
 *     `Emitter<T>` rather than `event EventHandler<T>`.
 *   - The Cognito-specific signup / reset methods that live on
 *     `ICognitoIdentityProvider` in Unity collapse onto this single
 *     interface. The intent is the same; we just don't have the
 *     LearnerCode / OIDC duality the Unity desktop ships, so a single
 *     interface is enough.
 *   - We expose `getIdToken(forceRefresh?)` instead of leaking a
 *     `CredentialsInfo` struct — that's the only token bit the rest of
 *     the app reads (sent as `Authorization` on cloud requests).
 *   - No `LearnerCode` / no `ExternalProjectIdentity` until those flows
 *     are scoped on the web. They can be added later without breaking
 *     callers.
 */
export interface IAuthModel {
  // ---- State ---------------------------------------------------------

  /** Whether `initializeAsync` has resolved at least once. */
  readonly isInitialized: boolean;

  /** Currently authenticated user, or `null` when signed out. */
  readonly currentUser: UserInfo | null;

  readonly identityProvider: IdentityProvider;

  /** True iff `currentUser != null`. Cheap shortcut for guards / templates. */
  readonly isAuthenticated: boolean;

  // ---- Events --------------------------------------------------------

  /** Emitted exactly once when `initializeAsync` completes. */
  readonly onInitialized: Emitter<void>;

  /** User logged in (after a fresh sign-in or a successful cached restore). */
  readonly onSignedIn: Emitter<UserInfo>;

  /** User explicitly logged out. */
  readonly onSignedOut: Emitter<void>;

  /** Tokens were refreshed (proactively or because a request was 401'd). */
  readonly onTokensRefreshed: Emitter<AuthTokens>;

  /**
   * Refresh failed and we couldn't recover — UI should treat as logout.
   * Mirrors Unity's `LoginWithWrongEmail` + cleared-credentials paths.
   */
  readonly onSessionExpired: Emitter<void>;

  // ---- Lifecycle -----------------------------------------------------

  /**
   * Configure the underlying provider (Amplify) and try to restore an
   * existing session. Idempotent. Resolves with the restored user
   * (if any) or `null`.
   */
  initializeAsync(): Promise<UserInfo | null>;

  /**
   * Proactively refresh the provider tokens after the app resumes from a
   * long browser sleep. A failed refresh expires the current session.
   */
  ensureFreshTokens(): Promise<void>;

  // ---- Sign-in / sign-out -------------------------------------------

  signInAsync(req: SignInRequest): Promise<SignInResult>;

  signOutAsync(): Promise<void>;

  // ---- Sign-up -------------------------------------------------------

  signUpAsync(req: SignUpRequest): Promise<{ requiresConfirmation: boolean }>;

  confirmSignUpAsync(req: ConfirmSignUpRequest): Promise<void>;

  resendSignUpCodeAsync(email: string): Promise<void>;

  // ---- Password reset -----------------------------------------------

  resetPasswordAsync(req: ResetPasswordRequest): Promise<void>;

  confirmResetPasswordAsync(req: ConfirmResetPasswordRequest): Promise<void>;

  // ---- Tokens --------------------------------------------------------

  /**
   * Returns a fresh ID token to send as `Authorization` on cloud calls.
   * If `forceRefresh` is true (e.g. after a 401), bypasses the cache.
   */
  getIdToken(forceRefresh?: boolean): Promise<string | null>;
}

/** IOC token. Resolve with `Ioc.resolve<IAuthModel>(AUTH_MODEL)`. */
export const AUTH_MODEL = Symbol.for("Campfire.AuthModel");
